import React from 'react';
import SearchInput from '../ui/SearchInput';
import {Button} from '../ui/button';

interface SearchFilterBarProps {
    isolatedBlueprintId: string;
    isolatedBlueprintName: string | undefined;
    clearIsolation: () => void;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    showOnlyMyBlueprints: boolean;
    setShowOnlyMyBlueprints: (value: boolean) => void;
    onCreate: () => void;
}

// Blueprints toolbar (page lock 7 amendment, deepcontext-hclj — supersedes the
// deepcontext-pnxk two-row layout): pill search left (flex-1, capped width),
// "Show only my blueprints" checkbox + gradient Create pill right-aligned, all
// in one row. Isolation (?blueprintId=) takes the search slot with an
// accent-tinted band instead, matching the locked banner anatomy.
//
// -mt-[21px] pulls the row's centerline up to match the sidebar's Explore nav
// pill centerline (deepcontext-jlkv mechanism, mirrors Explore.tsx's
// `explore-hero` nudge): the content header (AppLayout.tsx) puts this row's
// top at 72px from the shell top (32px py-8 + 16px text-xs subtitle line +
// 24px mb-6), and the SearchInput pill's own half-height is 21px, so
// unadjusted the row's centerline lands at 93px — 21px below the sidebar's
// 72px target (52px brand block + 20px half pill height).
const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
    isolatedBlueprintId,
    isolatedBlueprintName,
    clearIsolation,
    searchTerm,
    setSearchTerm,
    showOnlyMyBlueprints,
    setShowOnlyMyBlueprints,
    onCreate,
}) => (
    <div className="-mt-[21px] flex flex-wrap items-center gap-3">
        {isolatedBlueprintId ? (
            /* bd-tkpeg — isolated to a single blueprint via ?blueprintId
               deep-link. Show a banner with a clear affordance instead of
               the text search (mirrors the ScheduledJobs ?jobId banner). */
            <div
                data-testid="blueprint-isolation-banner"
                className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-full border border-accent/40 bg-nav-active px-4 py-2"
            >
                <span className="text-xs font-semibold text-accent-active">
                    Showing one blueprint
                    {isolatedBlueprintName ? `: ${isolatedBlueprintName}` : ' (not found)'}
                </span>
                <button
                    type="button"
                    onClick={clearIsolation}
                    className="text-xs font-bold text-accent-active underline hover:no-underline"
                >
                    Show all blueprints
                </button>
            </div>
        ) : (
            <SearchInput
                className="flex-1"
                placeholder="Search blueprints by name, description, content, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
        )}

        <div className="ml-auto flex flex-shrink-0 items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink-muted">
                <input
                    type="checkbox"
                    checked={showOnlyMyBlueprints}
                    onChange={(e) => setShowOnlyMyBlueprints(e.target.checked)}
                    className="h-3.5 w-3.5 rounded-[5px] border border-ink-muted accent-accent focus:ring-2 focus:ring-accent"
                />
                Show only my blueprints
            </label>
            <Button type="button" onClick={onCreate} className="whitespace-nowrap">
                Create Blueprint
            </Button>
        </div>
    </div>
);

export default SearchFilterBar;
